// One definition of "a slot", shared by the calendar grid and the booking
// form's time chips. They used to compute their own: the grid started at 7am
// and stepped by the studio's row length, the chips anchored on 9am, so a
// 45-minute studio saw rows at 9:15 and chips at 9:00 and the two never
// lined up.

// The first hour the grid draws when a studio hasn't chosen its own (Edit
// Studio → "Day starts at", studios.day_start_minutes). FullCalendar's
// slotMinTime for every time-grid view, and therefore the phase every slot
// is measured from.
export const GRID_START_HOUR = 7;

/** The studio's chosen start, in minutes from midnight, or 7 am. */
export const dayStartOr = (minutes) =>
  Number.isFinite(Number(minutes)) && Number(minutes) > 0 ? Number(minutes) : GRID_START_HOUR * 60;

// The last start a chip is offered for. Later lessons are still bookable on
// the Clock tab, and an existing one keeps its own chip.
export const GRID_END_HOUR = 22;

/**
 * How long one row of the grid is, in minutes, for a studio whose lessons
 * are `lessonMinutes` long.
 *
 * gcd(lesson length, 60) always divides the hour, so axis labels stay round
 * (a 75-minute studio reads 9am then 10am, not 8:15 then 9:30), and always
 * divides the lesson, so every lesson spans whole rows. 45 is the deliberate
 * exception: it keeps the app's long-standing 45-minute rows instead of
 * dropping to a 15-minute grid four times as tall.
 */
export const gridSlotMinutes = (lessonMinutes) => {
  const minutes = Number(lessonMinutes) > 0 ? Number(lessonMinutes) : 45;
  if (minutes === 45) return 45;
  const gcd = (a, b) => (b === 0 ? a : gcd(b, a % b));
  return gcd(minutes, 60);
};

/**
 * Where the grid starts for what is on it, in minutes from midnight.
 *
 * The studio's day start (7 am unless it chose another) unless something on
 * the visible days starts earlier. Then the grid backs up in whole rows until the earliest start is on the sheet,
 * so the rows keep their phase and the booking form's chips still land on
 * them (a 45-minute studio with a 6:15 lesson starts at 6:15, not 6:00;
 * with a 6:00 lesson it starts at 5:30). Stefan, call 3, 25:08: "if by some
 * magical reason you have an earlier slot, there's a whole line added to
 * that day."
 *
 * `range` is the visible window ({ start, end }, end exclusive); without
 * one the grid starts at 7. Day-spanning blocks and all-day events are
 * skipped: they start at midnight and would drag every day down to 12 am.
 */
export const gridStartMinutesFor = (events, range, lessonMinutes, dayStartMinutes) => {
  const step = gridSlotMinutes(lessonMinutes);
  let startMinutes = dayStartOr(dayStartMinutes);
  if (!range || !range.start || !range.end) return startMinutes;
  const from = new Date(range.start).getTime();
  const to = new Date(range.end).getTime();
  let earliest = Infinity;
  (events || []).forEach((event) => {
    if (!event || event.allDay || event.extendedProps?.allDay) return;
    const start = event.start instanceof Date ? event.start : new Date(event.start);
    const t = start.getTime();
    if (Number.isNaN(t) || t < from || t >= to) return;
    earliest = Math.min(earliest, start.getHours() * 60 + start.getMinutes());
  });
  while (startMinutes > earliest && startMinutes - step >= 0) startMinutes -= step;
  if (startMinutes > earliest) startMinutes = 0;
  return startMinutes;
};

/** Minutes from midnight as FullCalendar's "H:mm:ss" duration string. */
export const slotTime = (minutes) =>
  `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, "0")}:00`;

/** Every bookable start on a given day, in the grid's own phase. */
export const slotsForDay = (day, lessonMinutes, dayStartMinutes) => {
  const step = gridSlotMinutes(lessonMinutes);
  const base = new Date(day);
  base.setHours(0, 0, 0, 0);
  const slots = [];
  for (
    let minutes = dayStartOr(dayStartMinutes);
    minutes + step <= GRID_END_HOUR * 60;
    minutes += step
  ) {
    // By the wall clock, not milliseconds from midnight: the day the clocks
    // change has 23 or 25 hours, which moved every chip an hour (Nov 1 2026).
    slots.push(new Date(base.getFullYear(), base.getMonth(), base.getDate(), 0, minutes));
  }
  return slots;
};
